import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import moment from "moment";
import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import VLFCard from "@/components/Card";
import MainActions from "@/components/MainActions";
import VLFDatePicker from "@/components/Input/DatePicker";
import {SeasonAPI, refreshSeasons} from "@/api/season";
import ArrowBack from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import {getTextField} from "@/utils/functions";
import {
	SUCCESSFUL_OPERATION,
	ERROR_OPERATION,
	REQUIRED_FIELD,
} from "@/utils/constants";

export default function HandleSettingsSeasonPage({edit, handleNotification}) {
	const navigate = useNavigate();
	const {id} = useParams();
	const [loading, setLoading] = useState(!!edit);
	const [errors, setErrors] = useState([]);
	const [fields, setFields] = useState({
		name: "",
		startDate: null,
		endDate: null,
	});

	useEffect(() => {
		if (!edit) return;
		const load = async () => {
			setLoading(true);
			try {
				const season = await SeasonAPI.getById(id);
				setFields({
					name: season.name,
					startDate: moment(season.startDate).toDate(),
					endDate: moment(season.endDate).toDate(),
				});
			} catch {
				handleNotification(ERROR_OPERATION, "error");
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [edit, id]); // eslint-disable-line react-hooks/exhaustive-deps

	const save = async (event) => {
		event.preventDefault();
		const invalid = [];
		if (!fields.name?.trim()) invalid.push("name");
		for (const key of ["startDate", "endDate"]) {
			if (!fields[key] || !moment(fields[key]).isValid()) invalid.push(key);
		}
		setErrors(invalid);
		if (invalid.length) return;
		if (moment(fields.endDate).isBefore(fields.startDate, "day")) {
			setErrors(["endDate"]);
			handleNotification(
				"La fine della stagione deve essere successiva o uguale all'inizio.",
				"error",
			);
			return;
		}
		setLoading(true);
		try {
			const body = {
				name: fields.name.trim(),
				startDate: moment(fields.startDate).format("YYYY-MM-DD"),
				endDate: moment(fields.endDate).format("YYYY-MM-DD"),
			};
			if (edit) await SeasonAPI.update(id, body);
			else await SeasonAPI.create(body);
			await refreshSeasons();
			navigate("/settings/seasons");
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const actions = [
		{
			icon: <ArrowBack />,
			title: "Indietro",
			onClick: () => navigate("/settings/seasons"),
			color: "info",
		},
		{icon: <SaveIcon />, title: "Salva", onClick: save},
	];
	const textField = {values: fields, setValues: setFields, errors, setErrors};
	const dateField = (key, label) => (
		<VLFDatePicker
			label={label}
			required
			value={fields[key]}
			onChange={(value) => {
				setFields({...fields, [key]: value});
				setErrors(errors.filter((error) => error !== key));
			}}
			error={errors.includes(key)}
			helperText={REQUIRED_FIELD}
		/>
	);

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={[{label: "Stagioni", path: "/settings/seasons"}]}
				actions={<MainActions actions={actions} />}
			/>
			<div className="sheet-container">
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<VLFCard
							openable={false}
							title="Informazioni principali"
							content={
								<div className="row">
									<div className="col-12 col-md-6">
										{getTextField({
											...textField,
											fieldKey: "name",
											label: "Nome",
											required: true,
										})}
									</div>
									<div className="col-12 col-md-3">
										{dateField("startDate", "Data inizio")}
									</div>
									<div className="col-12 col-md-3">
										{dateField("endDate", "Data fine")}
									</div>
								</div>
							}
						/>
					</div>
				</div>
			</div>
		</Page>
	);
}
